import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Divider,
  Table,
  Tbody,
  Tr,
  Td,
  useColorModeValue,
  Alert,
  AlertIcon,
  Badge,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { MdPerson, MdTrendingUp, MdTrendingDown } from 'react-icons/md';

export default function PayrollPreviewModal({
  isOpen,
  onClose,
  previewData,
  onConfirm,
  loading,
}) {
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const bgGreen = useColorModeValue('green.50', 'green.900');
  const sectionBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  if (!previewData) return null;

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-UY', {
      style: 'currency',
      currency: 'UYU',
      minimumFractionDigits: 2,
    }).format(amount || 0);

  const br = previewData.breakdown || {};
  const bonusManual = br.bonusBenefits ?? previewData.bonuses ?? 0;
  const absenceDays = br.absenceDays ?? previewData.absenceDays ?? 0;
  const absenceAmt = Number(br.absence || 0);
  const absenceEmbedded = Boolean(br.absenceEmbeddedInBase);
  const workedDaysPaid = br.workedDaysPaid ?? previewData.workedDays;
  const ipd = br.ipd;

  const pctLabel = (dec) => {
    if (dec == null || dec === '' || Number.isNaN(Number(dec))) return null;
    const n = Number(dec);
    return `${(n * 100).toLocaleString('es-UY', { maximumFractionDigits: 3 })} %`;
  };

  const baseLabel =
    absenceEmbedded && absenceDays > 0
      ? `Sueldo proporcional (${workedDaysPaid}/${br.monthlyBaseDays ?? 30} d., base 30)`
      : 'Salario Base';

  const haberesRows = [
    { label: baseLabel, value: previewData.baseSalary, show: true },
    {
      label: absenceDays > 0 ? `Ausencias (${absenceDays} d.) — equivalente` : 'Ausencias',
      value: -absenceAmt,
      show: absenceDays > 0 && absenceAmt > 0 && !absenceEmbedded,
      valueColor: 'orange.600',
    },
    { label: 'Horas Extra', value: previewData.overtimeAmount, show: previewData.overtimeAmount > 0 },
    { label: 'Bonificaciones', value: bonusManual, show: Number(bonusManual) > 0 },
    { label: 'Antigüedad', value: br.seniority, show: (br.seniority ?? 0) > 0 },
    { label: 'Aguinaldo', value: br.aguinaldo, show: (br.aguinaldo ?? 0) > 0, badge: 'teal' },
    { label: 'Indemnización (cese)', value: br.severance, show: (br.severance ?? 0) > 0, badge: 'orange' },
    { label: 'Preaviso', value: br.notice, show: (br.notice ?? 0) > 0, badge: 'orange' },
    { label: 'Licencia no gozada', value: br.vacationPayout, show: (br.vacationPayout ?? 0) > 0, badge: 'purple' },
    { label: 'Salario vacacional', value: br.vacationBonus, show: (br.vacationBonus ?? 0) > 0, badge: 'purple' },
  ];

  const deduccionRows = [
    // IRPF lo mostramos aunque sea 0 para que sea visible en la liquidación.
    { label: 'IRPF', value: Number(br.irpf || 0), color: 'red.500', always: true },
    {
      label: br.bpsRate != null && !Number.isNaN(Number(br.bpsRate)) ? `BPS (${pctLabel(br.bpsRate)})` : 'BPS',
      value: br.bps,
      color: 'orange.500',
    },
    {
      label: br.fonasaRate != null && !Number.isNaN(Number(br.fonasaRate)) ? `FONASA (${pctLabel(br.fonasaRate)})` : 'FONASA',
      value: br.fonasa,
      color: 'blue.500',
    },
    {
      label: br.frlRate != null && !Number.isNaN(Number(br.frlRate)) ? `FRL (${pctLabel(br.frlRate)})` : 'FRL',
      value: br.frl,
      color: 'purple.500',
    },
    { label: 'Adelantos', value: br.advance, color: 'red.400' },
    { label: 'Préstamos', value: br.loan, color: 'red.400' },
    { label: 'Otras deducciones', value: br.otherDeductions, color: 'red.400' },
  ].filter((r) => r.always || r.value > 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent borderRadius="xl">
        <ModalHeader pb={2}>
          <Text fontSize="lg" fontWeight="700">Vista Previa de Liquidación</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">
            {previewData.hasExisting && (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Ya existe una liquidación para este período. Si confirma, será reemplazada.
              </Alert>
            )}

            <Flex align="center" gap={3} p={3} bg={sectionBg} borderRadius="lg">
              <Icon as={MdPerson} w={5} h={5} color="brand.500" />
              <Box>
                <Text fontWeight="600" fontSize="md">{previewData.employeeName}</Text>
                <Text fontSize="xs" color="gray.500">
                  Días trabajados (devengados): {workedDaysPaid}
                  {absenceEmbedded && absenceDays > 0
                    ? ` · ${absenceDays} ausencia(s) ya restan del proporcional (base 30)`
                    : ''}
                </Text>
              </Box>
            </Flex>

            <Box>
              <Flex align="center" gap={2} mb={3}>
                <Icon as={MdTrendingUp} color="green.500" />
                <Text fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color="green.600">
                  Haberes
                </Text>
              </Flex>
              <Table size="sm" variant="unstyled">
                <Tbody>
                  {haberesRows.filter((r) => r.show).map((row, i) => (
                    <Tr key={i}>
                      <Td py={2} px={3} borderBottom="1px" borderColor={borderColor}>
                        <Flex align="center" gap={2}>
                          <Text fontSize="sm" color={textColor}>{row.label}</Text>
                          {row.badge && <Badge colorScheme={row.badge} fontSize="2xs" variant="subtle">{row.label.split(' ')[0]}</Badge>}
                        </Flex>
                      </Td>
                      <Td
                        py={2}
                        px={3}
                        isNumeric
                        fontWeight="500"
                        fontSize="sm"
                        borderBottom="1px"
                        borderColor={borderColor}
                        color={row.valueColor || textColor}
                      >
                        {formatCurrency(row.value)}
                      </Td>
                    </Tr>
                  ))}
                  {/* El bruto ya está representado por “Salario Base + Haberes” y las deducciones. */}
                </Tbody>
              </Table>

              {ipd && ipd.sueldoReferenciaMonthly > 0 && (
                <Box mt={3} p={3} bg={sectionBg} borderRadius="md">
                  <Text fontSize="xs" color="gray.500" lineHeight="1.4">
                    IPD: {ipd.antiguedadOriginalMonths} meses = {Number(ipd.antiguedadOriginalYears || 0).toFixed(2)} años · años enteros {ipd.yearsEnteros} · meses restantes {ipd.mesesRestantes} · fracción {ipd.fraccionAplicada ? '>6 (suma +1 año)' : '<=6 (no suma)'} · tope {formatCurrency(ipd.ipdTopeRaw)} {ipd.topeAplicado ? '(aplicado)' : ''}
                  </Text>
                </Box>
              )}
            </Box>

            <Divider />

            <Box>
              <Flex align="center" gap={2} mb={3}>
                <Icon as={MdTrendingDown} color="red.500" />
                <Text fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color="red.500">
                  Deducciones
                </Text>
              </Flex>
              <Table size="sm" variant="unstyled">
                <Tbody>
                  {deduccionRows.map((row, i) => (
                    <Tr key={i}>
                      <Td py={2} px={3} borderBottom="1px" borderColor={borderColor}>
                        <Text fontSize="sm" color={textColor}>{row.label}</Text>
                      </Td>
                      <Td py={2} px={3} isNumeric fontWeight="500" fontSize="sm" color={row.color} borderBottom="1px" borderColor={borderColor}>
                        -{formatCurrency(row.value)}
                      </Td>
                    </Tr>
                  ))}
                  <Tr>
                    <Td py={3} px={3} fontWeight="700" fontSize="sm">Total Deducciones</Td>
                    <Td py={3} px={3} isNumeric fontWeight="700" fontSize="md" color="red.600">
                      -{formatCurrency(previewData.totalDeductions)}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            <Box p={5} bg={bgGreen} borderRadius="lg" borderWidth="2px" borderColor="green.300">
              <HStack justify="space-between">
                <Text fontSize="lg" fontWeight="700">Salario Líquido</Text>
                <Text fontSize="2xl" fontWeight="800" color="green.600">
                  {formatCurrency(previewData.netSalary)}
                </Text>
              </HStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px" borderColor={borderColor} pt={4}>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={loading} size="md">
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={onConfirm} size="md">
              {previewData.hasExisting ? 'Confirmar y Reemplazar' : 'Confirmar y Guardar'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
